import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import Select from 'react-select';
import { useAuth0 } from '@auth0/auth0-react';
import { config } from '../../config';

interface Option {
  value: string;
  label: string;
}

interface TagType {
  tag_type: string;
  values?: string[];
}

interface Assessment {
  id: string;
  name: string;
  values?: any[];
}

interface SeriesDropdownProps {
  onSeriesChange: (
    seriesType: string,
    tagType: string | null,
    assessmentId: string | null
  ) => void;
  placeholder?: string;
  ready?: boolean;
  selectedAccount: number | null;
}

const CACHE_DURATION = 4 * 60 * 60 * 1000; // 4 hours
const TAGS_CACHE_KEY = 'ept_response_tags_cache';
const ASSESSMENTS_CACHE_KEY = 'ept_response_assessments_cache';

const SeriesDropdown: React.FC<SeriesDropdownProps> = ({
  onSeriesChange,
  placeholder = 'Select Series (Optional)',
  ready = true,
  selectedAccount,
}) => {
  const { getIdTokenClaims } = useAuth0();
  const [seriesType, setSeriesType] = useState<string>('channels');
  const [selectedTagType, setSelectedTagType] = useState<Option | null>(null);
  const [selectedAssessment, setSelectedAssessment] = useState<Option | null>(
    null
  );

  // Options data
  const [tagTypes, setTagTypes] = useState<Option[]>([]);
  const [assessments, setAssessments] = useState<Option[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadedOnce, setLoadedOnce] = useState<boolean>(false);
  const fetchingRef = React.useRef({ tags: false, assessments: false });

  const seriesOptions: Option[] = [
    { value: 'channels', label: 'By Channel' },
    { value: 'tags', label: 'By Tag Type' },
    { value: 'assessments', label: 'By Assessment' },
  ];

  // Get cached data
  const getCachedData = (key: string) => {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp > CACHE_DURATION) {
        localStorage.removeItem(key);
        return null;
      }
      return data;
    } catch {
      return null;
    }
  };

  // Set cached data
  const setCachedData = (key: string, data: any) => {
    try {
      localStorage.setItem(
        key,
        JSON.stringify({ data, timestamp: Date.now() })
      );
    } catch {}
  };

  // Fetch tag types
  const fetchTagTypes = async () => {
    if (fetchingRef.current.tags) return;
    fetchingRef.current.tags = true;

    try {
      const cached = getCachedData(TAGS_CACHE_KEY);
      if (cached) {
        setTagTypes(cached);
        fetchingRef.current.tags = false;
        return;
      }

      const getidToken = await getIdTokenClaims();
      const idToken = getidToken?.__raw;
      const options = {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      };
      const response = await fetch(
        `${config.baseURL}/admin/v1/response/tags?account_id=${selectedAccount}`,
        options
      );
      if (response.ok) {
        const result: { tag_types?: TagType[] } = await response.json();
        const types = result.tag_types || [];
        const mapped = types.map(t => ({
          value: t.tag_type,
          label: t.tag_type,
        }));
        setTagTypes(mapped);
        setCachedData(TAGS_CACHE_KEY, mapped);
      }
    } catch (error) {
      console.error('Error fetching tag types:', error);
    } finally {
      fetchingRef.current.tags = false;
    }
  };

  // Fetch assessments
  const fetchAssessments = async () => {
    if (fetchingRef.current.assessments) return;
    fetchingRef.current.assessments = true;

    try {
      const cached = getCachedData(ASSESSMENTS_CACHE_KEY);
      if (cached) {
        setAssessments(cached);
        fetchingRef.current.assessments = false;
        return;
      }

      const getidToken = await getIdTokenClaims();
      const idToken = getidToken?.__raw;
      const options = {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      };
      const response = await fetch(
        `${config.baseURL}/admin/v1/response/assessments`,
        options
      );
      if (response.ok) {
        const result = await response.json();
        const items = result.assessments || [];
        if (!Array.isArray(items)) {
          fetchingRef.current.assessments = false;
          return;
        }
        const mappedAssessments = items
          .filter((a: any) => a && a.id && a.name)
          .map((a: any) => ({
            value: String(a.id),
            label: String(a.name),
          }));
        setAssessments(mappedAssessments);
        setCachedData(ASSESSMENTS_CACHE_KEY, mappedAssessments);
      }
    } catch (error) {
      console.error('Error fetching assessments:', error);
    } finally {
      fetchingRef.current.assessments = false;
    }
  };

  // Lazy-load options only once after initial chart load is ready
  useEffect(() => {
    if (!ready || loadedOnce) return;
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        await Promise.all([fetchTagTypes(), fetchAssessments()]);
        if (mounted) setLoadedOnce(true);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  // Handle series type change
  const handleSeriesTypeChangeSelect = (option: Option | null) => {
    const newType = option?.value || 'channels';
    setSeriesType(newType);
    setSelectedTagType(null);
    setSelectedAssessment(null);

    if (newType === 'channels') {
      onSeriesChange('channels', null, null);
    } else if (newType === 'tags') {
      onSeriesChange('tags', null, null);
    } else if (newType === 'assessments') {
      onSeriesChange('assessments', null, null);
    }
  };

  // Handle tag type selection
  const handleTagTypeChange = (option: Option | null) => {
    setSelectedTagType(option);
    if (option && seriesType === 'tags') {
      onSeriesChange('tags', option.value, null);
    } else if (!option && seriesType === 'tags') {
      setSeriesType('channels');
      onSeriesChange('channels', null, null);
    }
  };

  // Handle assessment selection
  const handleAssessmentChange = (option: Option | null) => {
    setSelectedAssessment(option);
    if (option && seriesType === 'assessments') {
      onSeriesChange('assessments', null, option.value);
    } else if (!option && seriesType === 'assessments') {
      setSeriesType('channels');
      onSeriesChange('channels', null, null);
    }
  };

  return (
    <div className="series-dropdown-container">
      <Form.Group style={{ marginBottom: '16px' }}>
        <Form.Label
          style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}
        >
          Series Type
        </Form.Label>
        <Select
          value={seriesOptions.find(o => o.value === seriesType)}
          onChange={handleSeriesTypeChangeSelect}
          options={seriesOptions}
          isSearchable={false}
          menuPlacement="bottom"
          menuPortalTarget={document.body}
          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          isDisabled={loading}
        />
      </Form.Group>

      {seriesType === 'tags' && (
        <Form.Group style={{ marginBottom: 0 }}>
          <Form.Label
            style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}
          >
            Tag Type
          </Form.Label>
          <Select
            value={selectedTagType}
            onChange={handleTagTypeChange}
            options={tagTypes}
            isSearchable
            isClearable
            placeholder={placeholder}
            isDisabled={loading || !ready}
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
        </Form.Group>
      )}

      {seriesType === 'assessments' && (
        <Form.Group style={{ marginBottom: 0 }}>
          <Form.Label
            style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}
          >
            Assessment
          </Form.Label>
          <Select
            value={selectedAssessment}
            onChange={handleAssessmentChange}
            options={assessments}
            isSearchable
            isClearable
            placeholder="Select Assessment"
            isDisabled={loading || !ready}
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
        </Form.Group>
      )}
    </div>
  );
};

export default SeriesDropdown;

